import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useNavigation,
  useFetcher,
  useNavigate,
  useFetchers,
  useLocation
} from "react-router";
import type { Route } from "./+types/root";
import styles from './app.css?url'
import loadingStyles from './loading.css?url'
import calenderStyle from 'react-calendar/dist/Calendar.css?url'
import uiKitStyle from 'react-essentials-ui-kit/src/style/style.css?url'
import ProgressBar from "niral/component/progressBar";
import { useEffect, useState } from "react";
// Import Sentry
import * as Sentry from '@sentry/react';
import React from 'react';
import { Device } from "@capacitor/device";
import {  util, type DeviceType } from "./utils/util";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from '@capacitor/splash-screen';
import NetworkStatusNotifier from "./utils/common/hooks/networkStatusNotifier";
import AppVersionBanner from "./components/appVersionBanner";
import { SWRConfig } from "swr";
import { constants } from "./utils/constants";
import ValidateEmployeeSession from "./routes/resources/validateEmployeeSession";
import { useFrappeTokenManager } from "./routes/login/frappeAuth";
import { Button, toast, Toaster } from "@fr8/frui";


// Initialize Sentry
const dsn = import.meta.env.VITE_SENTRY_DSN
 if (dsn) {
  Sentry.init({
    dsn: dsn,
    // Performance monitoring
    tracesSampleRate: 0,
    autoSessionTracking: false,
    sendClientReports: false,
    // Set to 1.0 for development
    environment: import.meta.env.VITE_NODE_ENV
  });
 }

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  { rel: "stylesheet", href: styles },
  { rel: "stylesheet", href: loadingStyles },
  { rel: 'stylesheet', href: uiKitStyle },
  { rel: 'stylesheet', href: calenderStyle },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const navigation = useNavigation()
  const isLoading = ['loading', 'submitting'].includes(navigation.state);
  const fetchers = useFetchers();
  const location = useLocation()

  const isFetcherAction = fetchers.some(
    (f) => {
      return f.state === 'submitting' &&
        f.formMethod?.toLowerCase() !== "get"
    }
  );
  const webLogin = location.pathname.endsWith("web-login");
  const isActionLoading = isFetcherAction && !webLogin;

  const platform = Capacitor.getPlatform();
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=no" />
        <Meta />
        <Links />
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      </head>
      <body>
        {/* <Theme> */}
        <Sentry.ErrorBoundary fallback={<FallbackComponent />}>
          {children}
        </Sentry.ErrorBoundary>
        <ScrollRestoration />
       <Toaster position={platform ==="web" ? "bottom-right": "top-center"} richColors />
        {(isLoading || isActionLoading) ? <ProgressBar isActionLoading={isActionLoading} /> : null}
        <Scripts />
      </body>
    </html>
  );
}

// Custom fallback component for the Sentry error boundary
function FallbackComponent() {
  const navigate = useNavigate();
  return (
    <main className="pt-16 p-4 h-screen container mx-auto">
      <h1>Something went wrong</h1>
      <p>An unexpected error occurred and has been reported to our team.</p>
      <div className="flex items-center h-[80vh] justify-center">
        <Button onClick={() => navigate('/')}>
          Return Home
        </Button>
      </div>
    </main>
  );
}


const DUPLICATE_LINK_REGEX = /^(.* for )([^,]+), (\/employee\/(?:partner\/[^,\s]+|partner_sales\/lead\/\d+))$/

export default function App() {
  const fetcher = useFetcher();
  const [blockMobileWeb, setBlockMobileWeb] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useFrappeTokenManager();

  useEffect(() => {
    detectPlatformAndInit(fetcher, setBlockMobileWeb);
  }, []);

  if (blockMobileWeb === null) return null;

  if (blockMobileWeb) {
    return <BlockedMobileWebView />;
  }  
  

  return (
    <>
      <SWRConfig value={{
        revalidateOnFocus: false, onError: (err) => {
          const message = err?.message ?? err?.cause?.failure?.error?.message ?? "Something went wrong"
          const match = DUPLICATE_LINK_REGEX.exec(message)
          if (match) {
            const [, prefix, identifier, path] = match
            toast.error(
              <span>
                {prefix}
                <a href={path} onClick={(e) => { e.preventDefault(); navigate(path) }} className="underline">
                  {identifier}
                </a>
              </span>
            )
            return
          }
          toast.error(message)
        }
      }} >
        <NetworkStatusNotifier />
        <AppVersionBanner />
        <Outlet />
        <ValidateEmployeeSession /> 
        </SWRConfig>
    </>
  );
}

function detectPlatformAndInit(
  fetcher: ReturnType<typeof useFetcher>,
  setBlockMobileWeb: React.Dispatch<React.SetStateAction<boolean | null>>
) {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;

  if (isMobileBrowser(ua) && !Capacitor.isNativePlatform()) {
    setBlockMobileWeb(true);
  } else {
    setBlockMobileWeb(false);
  }

  initApp(fetcher, ua);
}

function isMobileBrowser(ua: string) {
  if (!/android|mobile/i.test(ua)) return false;
  if (/ipad|tablet/i.test(ua)) return false;
  if (/iphone|ipod/i.test(ua)) return false;
  if (typeof window !== "undefined" && Math.min(window.screen.width, window.screen.height) >= 600) return false;
  return true;
}


async function initApp(fetcher: ReturnType<typeof useFetcher>, ua: string) {
  await SplashScreen.hide();
  await setDeviceCookies(ua);
}

async function setDeviceCookies(ua: string) {
  const deviceInfo = await Device.getInfo();
  const isApple = /iphone|ipod|iPad/i.test(ua);
  const info = isApple ? { platform: "mobile" } : deviceInfo;

  util.setCookie("devicePlatform", info.platform, 7);
  const deviceTypeUserAgent: DeviceType = util.getDeviceTypeFromUserAgent(navigator.userAgent);
  util.setCookie("deviceType", deviceTypeUserAgent, 7);
}

function BlockedMobileWebView() {
  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <h2>Access Restricted</h2>
      <p>This service is only accessible via the mobile app on your phone.</p>
      {/* isMobileBrowser() (above) excludes iPhone/iPod, so this is only ever reached from an Android mobile browser. */}
      <a href={constants.APP_VERSION_URL.android.downloadUrl}>
        Download the app
      </a>
    </div>
  );
}


// Update the error boundary to send errors to Sentry
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  const allowedErrors = [isRouteErrorResponse(error) && error?.status === 404]
  const skipSentry = allowedErrors.some(value => !!value)
  if(!skipSentry) {
    // Send error to Sentry
    Sentry.captureException(error);
  }

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  const navigate = useNavigate()

  return (
      <main className="pt-16 p-4 h-screen container mx-auto">
        <h1>{message}</h1>
        <p>{details}</p>
        {stack && (
          <pre className="w-full p-4 overflow-x-auto">
            <code>{stack}</code>
          </pre>
        )}

        <div className="flex items-center h-[80vh] justify-center">
          <Button onClick={() => navigate('/')}>
            Home
          </Button>
        </div>
      </main>
  );
}